import { defineComponent, computed } from 'vue-demi'
import { isNumber } from '@svoi-ui/shared/utils/guards'
import type { PageInfo } from '@svoi-ui/interfaces/pagination'

/* eslint-disable-next-line @typescript-eslint/naming-convention */
export const BasePaginationComponent = defineComponent({
  name: 'BasePagination',
  props: {
    totalPages: {
      type: Number,
      default: 1,
      validator: (value: unknown) => {
        return isNumber(value) && value >= 0
      }
    },
    activePage: {
      type: Number,
      default: 1
    },
    maxPoints: {
      type: Number,
      default: 9,
      validator: (value: unknown) => {
        return value === 7 || value === 9
      }
    },
    showArrows: {
      type: Boolean,
      default: true
    }
  }
})

export const useBasePagination = (
  props: Readonly<{
    activePage: number
    totalPages: number
    maxPoints: number
  }>
) => {
  const prepareActivePage = computed(() => Math.max(Math.min(props.activePage, props.totalPages), 1))
  const maxInRow = computed(() => props.maxPoints - 4)

  const minPage = computed(() => {
    const offset = Math.floor(maxInRow.value / 2)
    let min = prepareActivePage.value - offset
    if (min + maxInRow.value >= props.totalPages) {
      min = props.totalPages - maxInRow.value + 1
    }
    if (prepareActivePage.value === min) {
      min--
    }
    min = Math.max(min, 1)
    return min === 2 ? 1 : min
  })

  const maxPage = computed(() => {
    let max = minPage.value + maxInRow.value - 1
    if (prepareActivePage.value === max) {
      max++
    }
    max = Math.min(max, props.totalPages)
    return max
  })

  const getLink = (baseUrl: string, page: number): string => {
    const hasHttp = /^http(s):\/\//.test(baseUrl)
    /* если базовый URL не полный, то подставляем к нему фиктивный домен, чтобы создать URL */
    const url = new URL(baseUrl, hasHttp ? '' : 'http://localhost')
    if (page === 1) {
      url.searchParams.delete('p')
    } else {
      url.searchParams.set('p', String(page))
    }
    return hasHttp ? url.href : `${url.pathname}${url.search}`
  }

  const getPages = (url: string) => {
    const pages: Array<PageInfo> = []

    if (minPage.value > 1) {
      pages.push({ title: 1, number: 1, link: getLink(url, 1) })
    }

    if (minPage.value > 2) {
      pages.push({ title: '...' })
    }

    for (let i = minPage.value; i <= maxPage.value; i++) {
      pages.push({ title: i, number: i, link: getLink(url, i) })
    }

    if (maxPage.value < props.totalPages - 1) {
      pages.push({ title: '...' })
    }

    if (maxPage.value < props.totalPages) {
      pages.push({ title: props.totalPages, number: props.totalPages, link: getLink(url, props.totalPages) })
    }

    return pages
  }

  return { prepareActivePage, maxInRow, minPage, maxPage, getLink, getPages }
}
